// Single sign-on hand-off from RegulApp.
//
// Regul links to PESTEL as `https://pestel.regul.app/#sso=<regul token>`. The
// fragment is deliberate: browsers never send it to a server, so the token
// stays out of access logs, Referer headers and CDN caches on the way here.
//
// We exchange it for a PESTEL JWT via `POST /api/auth/sso`, which verifies the
// token against Regul server-side. The Regul token itself is never stored.
import axios from 'axios'
import { pestelApiUrl, setAuthHeader } from './api'

const TOKEN_KEY = 'authToken'
const SSO_PARAM = 'sso'
// Why a hand-off was refused, handed to the login page to display. Session
// scoped: it is about this arrival, not something to remember.
export const SSO_ERROR_KEY = 'pestelSsoError'

function hashParams() {
  return new URLSearchParams((window.location.hash || '').replace(/^#/, ''))
}

// Drop the token from the address bar (and from history — replaceState, not
// pushState) so it can't be copy-pasted, bookmarked or read by a later page.
function stripSsoFromHash() {
  const params = hashParams()
  if (!params.has(SSO_PARAM)) {
    return
  }
  params.delete(SSO_PARAM)
  const rest = params.toString()
  const url = window.location.pathname + window.location.search + (rest ? `#${rest}` : '')
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, '', url)
  } else {
    window.location.hash = rest
  }
}

/**
 * Consume an `#sso=` hand-off if one is present.
 *
 * Resolves with the new PESTEL JWT, or null when there was no hand-off or the
 * exchange failed (in which case the app carries on with whatever session it
 * already had, and the route guards send the user to /login if there is none).
 *
 * Must run before the Redux store is created: the auth reducer seeds its
 * initial state from localStorage at module-evaluation time.
 */
export function consumeSsoToken() {
  const token = (hashParams().get(SSO_PARAM) || '').trim()
  if (!token) {
    return Promise.resolve(null)
  }
  // Strip first, so the token is gone from the URL even if the request hangs.
  stripSsoFromHash()
  try {
    sessionStorage.removeItem(SSO_ERROR_KEY)
  } catch (e) { /* private mode — the message is a nicety, not a requirement */ }

  return axios
    // Bounded: index.js blocks the app mount on this promise, so an
    // unbounded request would leave a blank page rather than a login form.
    .post(pestelApiUrl('/auth/sso'), { token }, { timeout: 15000 })
    .then((res) => {
      const accessToken = (res.data || {}).access_token
      if (!accessToken) {
        return null
      }
      localStorage.setItem(TOKEN_KEY, accessToken)
      setAuthHeader(accessToken)
      return accessToken
    })
    .catch((err) => {
      // A rejected hand-off is a real answer, not a glitch: most often the
      // person has a Regul account but no PESTEL seat. Carry the reason to the
      // login page so they are told what to do instead of facing a bare form.
      const data = (err && err.response && err.response.data) || {}
      if (data.error) {
        try {
          sessionStorage.setItem(SSO_ERROR_KEY, data.error)
        } catch (e) { /* see above */ }
      }
      return null
    })
}

export default consumeSsoToken
